import React from 'react';
import { Tooltip } from 'antd';
import { FormattedMessage } from 'react-intl';
import { format, getMonth, add } from 'date-fns';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { InfoSvg } from 'src/assets/index';
import { BlueButton } from 'src/components/components/index';
import {
  Routes,
  DATE_FORMAT_MONTH,
  DATE_FORMAT_MONTH_END,
  DATE_FORMAT_MONTH_START,
  DATE_FORMAT_MONTH_END_DIFFERENT,
  DATE_FORMAT_MONTH_START_DIFFERENT,
} from 'src/constants/index';
import { GetawaysLocationEnum, IGetaway } from 'src/common-types/index';
import { renderPriceBasedOn, renderGetawaysCardDate, formatMoney, getDateCheckInOut } from 'src/utils/index';
import GetawayBackground from 'src/assets/images/getaways/getaways-list-bg.png';
import './style.scss';

interface IProps extends RouteComponentProps {
  data: IGetaway;
  condoSubcategory: string;
}

interface IState {
  isError: boolean;
}

export class GetawayListCardComponent extends React.Component<IProps, IState> {
  state: IState = {
    isError: false,
  };

  onError = (event: React.SyntheticEvent<HTMLImageElement, Event>): void => {
    if (event) {
      this.setState({ isError: true });
    }
  };

  redirectToDetails = (): void => {
    const { data, condoSubcategory, history } = this.props;
    const specialType = data.specialType.specialTypeName.toLowerCase();

    history.push(
      `${Routes.GetawayDetails}/${data.promoId}/${specialType}/${condoSubcategory ? condoSubcategory : ''
      }`,
    );
  };

  render(): React.ReactNode {
    const { data } = this.props;
    const { isError } = this.state;
    const {
      city,
      name,
      title,
      state,
      country,
      promoId,
      unitSize,
      imageUrl,
      checkinDate,
      youPayPrice,
      specialType,
      portsOfCall,
      priceBasedOn,
      lengthOfStay,
      numberOfNights,
    } = data;
    const { specialTypeName } = specialType;
    const toLowercaseSpecialTypeName = specialTypeName.toLowerCase();
    const isTypeNameCondo = toLowercaseSpecialTypeName === GetawaysLocationEnum.Condo;
    const isTypeNameCruise = toLowercaseSpecialTypeName === GetawaysLocationEnum.Cruise;
    const isTypeNameFantasy = toLowercaseSpecialTypeName === GetawaysLocationEnum.Fantasy;
    const isTypeNameStaycation = toLowercaseSpecialTypeName === GetawaysLocationEnum.Staycation;
    const payPrice = isTypeNameStaycation ? youPayPrice / numberOfNights : youPayPrice;

    const checkInDate = getDateCheckInOut(checkinDate);
    const formattedDate = checkInDate && format(checkInDate, DATE_FORMAT_MONTH);
    const checkInDateWithNights = add(checkInDate, {
      days: numberOfNights,
    });
    const formattedStartDate = checkInDate && format(checkInDate, DATE_FORMAT_MONTH_START);
    const formattedStartDateDifferent =
      checkInDate && format(checkInDate, DATE_FORMAT_MONTH_START_DIFFERENT);
    const formattedEndDate = checkInDate && format(checkInDateWithNights, DATE_FORMAT_MONTH_END);
    const formattedEndDateDifferent =
      checkInDate && format(checkInDateWithNights, DATE_FORMAT_MONTH_END_DIFFERENT);

    const checkInEndDate = new Date(checkInDateWithNights);
    const startMonth = getMonth(checkInDate);
    const endMonth = getMonth(checkInEndDate);
    const isDifferentMonths = startMonth !== endMonth;
    const staycationDate = checkInDate && (isDifferentMonths
      ? `${formattedStartDate}-${formattedEndDate}`
      : `${formattedStartDateDifferent} - ${formattedEndDateDifferent}`);

    return (
      <div className="getaway__list-item">
        <img
          className="getaway__list-item_img"
          onError={(event) => this.onError(event)}
          src={isError || !imageUrl ? GetawayBackground : imageUrl}
          alt="img"
          loading="lazy"
        />
        <div className="getaway__list-item_info">
          <div>
            <div className="getaway__list-item_info_header">
              <p className="getaway__list-item_info_header_title">{title}</p>
              <p className="getaway__list-item_info_header_value">{formatMoney.format(payPrice)}</p>
            </div>
            <div className="getaway__list-item_info_content">
              <p className="getaway__list-item_info_content_text">
                <span>
                  <FormattedMessage id="promo.id" /> {promoId}
                </span>
              </p>
              <div className="getaway__list-item_info_content_per">
                <p className="getaway__list-item_info_content_per_title">
                  {renderPriceBasedOn(toLowercaseSpecialTypeName, false)}
                </p>
                <Tooltip
                  title={priceBasedOn}
                  overlayClassName="getaway__list-item_info_tooltip"
                  placement="bottom"
                >
                  <div className="bookings-title-wrapper">
                    <InfoSvg />
                  </div>
                </Tooltip>
              </div>
            </div>
            {isTypeNameCruise && (
              <>
                {portsOfCall.length ? (
                  portsOfCall.map((port: string, index: number) => (
                    <p className="getaway__list-item_info_location_cruise" key={`${port}-${index}`}>
                      {port}
                    </p>
                  ))
                ) : (
                  <p className="getaway__list-item_info_location_cruise">
                    <FormattedMessage id="no.data.available" />
                  </p>
                )}

                <p className="getaway__list-item_info_location_cruise">
                  <FormattedMessage id="ship" /> {name}
                </p>
              </>
            )}
            {isTypeNameCondo && (
              <p className="getaway__list-item_info_location">
                {city} {state} {country} | {unitSize}
              </p>
            )}
          </div>
          <div
            className={`getaway__list-item_info_date ${isTypeNameFantasy ? 'getaway__list-item_info_date_fantasy' : ''
              }`}
          >
            <p
              className={`getaway__list-item_info_date_text ${!!isTypeNameCruise && 'getaway__list-item_info_date_text_cruise'
                }`}
            >
              {renderGetawaysCardDate(
                toLowercaseSpecialTypeName,
                formattedDate,
                staycationDate,
                lengthOfStay,
                city,
                numberOfNights,
                state,
              )}
            </p>

            <BlueButton onClick={this.redirectToDetails}>
              <FormattedMessage id="view.details" />
            </BlueButton>
          </div>
        </div>
      </div>
    );
  }
}

export const GetawayListCard = withRouter(GetawayListCardComponent);
